import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { PropertyGrid, EmbeddedPropertyGridProps, IShowDialogAction } from 'components/property-editors/PropertyGrid';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { IEditorProps } from '../Editor';
import { createContextForProperty } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { EditorCategoryContext } from 'components/property-editors/PropertyGrid/EditorCategoryContext';
import { SkeletonLoader } from 'loaders/SkeletonLoader';
import { isAbortError } from 'utils/ErrorUtils';

interface IPropertyGridEmbeddedEditorProps extends IEditorProps {}

/**
 * An "editor" that is a Property Grid... so meta
 *
 * TODO At some point we should revisit the utility of this pattern... why is this more useful than embedding
 * these nodes in the original context snapshot the way we do with "UsePopupEditor" categories?
 * It certainly adds a lot of complexity to support this. Seems this is only ever used in Reports.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function PropertyGridEmbeddedEditor({ child, path }: IPropertyGridEmbeddedEditorProps) {
  const propertyEditorContext = useContext(PropertyEditorContext);
  const { reportChildCount, setPreserveChildren } = useContext(EditorCategoryContext);
  const { editingContextId, searchString } = propertyEditorContext;
  const prevEditingContextId = useRef('');
  const [embeddedContextId, setEmbeddedContextId] = useState('');
  const hideTopCategory = getEditorConfigValue(child, 'HideTopCategory');
  const forceRefresh = getEditorConfigValue(child, 'ForceRefresh') ?? false;
  const abort = useRef(new AbortController());
  const embeddedProps: EmbeddedPropertyGridProps = {
    hideTopCategory,
    parentSearch: searchString,
    reportChildCount: count => reportChildCount?.(hideTopCategory ? count - 1 : count),
  };
  // const hasUnmounted = useRef(false);
  // const shouldUpdateEmbeddedId = useState(false);

  useEffect(() => {
    setPreserveChildren(true);
  }, [setPreserveChildren]);

  /**
   * Invoke dialog but tell parent grid to use this editingContextId
   */
  const showDialog = useCallback(
    (action: IShowDialogAction) => {
      propertyEditorContext.onShowDialogue({ ...action, editingContextId: embeddedContextId });
    },
    // keep this callback unless embeddedContextId changes:
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [embeddedContextId]
  );

  const fetchEmbeddedId = useCallback(async (editingContextId: string, path: string[]) => {
    // console.warn('aborting', prevEditingContextId.current);
    abort.current.abort();
    console.log('Getting embedded context', editingContextId, path);

    // in case this has a previously in-flight request from a different context ID
    const abortController = new AbortController();
    abort.current = abortController;

    setEmbeddedContextId(''); // un-mount previous p-grid, just in case

    const id = await getEmbeddedContext(
      path,
      { contextProviderRegistrationId: editingContextId, path },
      abortController
    );

    if (editingContextId !== prevEditingContextId.current) {
      console.log('got new embedded context, parent ID was already updated', editingContextId, id);
      return;
    }

    if (abortController.signal.aborted || abort.current.signal.aborted) {
      console.log('got new embedded context, but signal is aborted', editingContextId, id);
      return;
    }
    // console.warn('setting embedded Id', id);
    setEmbeddedContextId(id || '');
  }, []);

  useEffect(() => {
    if (forceRefresh === false && prevEditingContextId.current === editingContextId) {
      return; // child node updated, but we're still working on the same context.
    }
    // console.warn('prevId', prevEditingContextId.current, 'currentId', editingContextId);
    prevEditingContextId.current = editingContextId;
    // fetchEmbeddedId.clear();
    fetchEmbeddedId(editingContextId, path);
    // only need a new one for a new property nodes coming in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child]);

  useEffect(() => {
    const abortController = abort.current;
    // abort.current = new AbortController();
    return () => {
      abortController?.abort();
    };
  }, []);

  // {"IsDataGridRowEditor","1"} - hides the _parent_ category in legacy ... or at least
  // the child count is accurate for something seemingly unrelated, so...

  return embeddedContextId ? (
    <PropertyGrid
      {...propertyEditorContext}
      editingContextId={embeddedContextId}
      embeddedProps={embeddedProps}
      onShowDialogue={showDialog}
    />
  ) : (
    <SkeletonLoader title={path[path.length - 1]} height={150} />
  );
}

type ArgsType<T> = T extends (...args: infer U) => any ? U : never;
type CreateContextArgs = ArgsType<typeof createContextForProperty>[0];

async function getEmbeddedContext(path: string[], parameters: CreateContextArgs, abort: AbortController) {
  try {
    return await createContextForProperty(parameters, abort);
    // the PropertyGrid will registerEditor when it loads, we just need to pass this embedded ID over
  } catch (error) {
    if (!isAbortError(error)) {
      console.error('Failed to Create context for property', path.join(', '), error);
    }
  }
}

// edit complete doesn't _seem to be called_ by legacy, so leaving this comments for now.
// async function onEditComplete(
//   path: string[],
//   parameters: {
//     editingContext: string;
//     completeType: EditCompleteType;
//   }
// ) {
//   try {
//     await editComplete(parameters);
//   } catch (error) {
//     console.error('Failed to commit EditComplete', path.join(', '), parameters.completeType);
//   }
// }

// cross reference
// @Html.Partial(
//  "~/Views/Action/PropertyGrid/PropertyGridView.cshtml",
//  contextProviderNodeModel,
//  new ViewDataDictionary() { { "IsDynamicObject", true },{"IsDataGridRowEditor","1"} }
// )
